const KEY = 'ms_had_session';

/**
 * Distinguishes two `RequireAuth` failure cases that read identically off `/auth/me`
 * alone but mean different things to a staff member (SCREEN_SPECS.md I24's
 * "Session Expired (mid-task)" case, `ux-review`'s named defect: "dumped at a login
 * screen with the work lost"):
 *
 *   - **Never had a session this browser tab** (a bookmarked deep link, a shared
 *     machine) — a plain redirect to `/login` is the honest, correct behaviour; there is
 *     nothing to explain because nothing was ever in progress.
 *   - **Had a session, and it just stopped working** (the absolute/idle timeout elapsed
 *     while a form was open) — this is the case that deserves its own screen: "Your
 *     session has expired" rather than the bare login form reappearing with no
 *     explanation, as if the click that triggered it had simply done nothing.
 *
 * `sessionStorage` (not a React ref/state) on purpose: it survives exactly one browser
 * tab's lifetime, which is the same lifetime the session cookie itself has in practice,
 * and it is readable from `RequireAuth` on the very first render after a hard reload —
 * a ref would forget "we were logged in" the moment the page reloads, which is precisely
 * when a lapsed session is most likely to be noticed.
 */
export function markHadSession(): void {
  try {
    sessionStorage.setItem(KEY, '1');
  } catch {
    // Storage disabled (private browsing edge cases) — the worst outcome is falling back
    // to the plain redirect, never a crash.
  }
}

export function hadSession(): boolean {
  try {
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

export function clearHadSession(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // See markHadSession.
  }
}
